import React, { useRef } from "react";
import "../css/chatMessage.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkSlug from "remark-slug";
import remarkAutolinkHeadings from "remark-autolink-headings";
import rehypeRaw from "rehype-raw";
import TableWithCopy from "./markdown/TableWithCopy";
import CodeBlockWithCopy from "./markdown/CodeBlockWithCopy";
import InlineCode from "./markdown/InlineCode";
import ImageWithZoom from "./markdown/ImageWithZoom";
import VideoWithZoom from "./markdown/VideoWithZoom";
import YouTubeEmbed from "./markdown/YouTubeEmbed";
import { branding } from "../utils/branding";
import fileIcon from "../assets/UserIcons/FileIcon.svg";
import downloadIcon from "../assets/UserIcons/DownloadIcon.svg";
import trainIcon from "../assets/Images/train_data.png";
import cpyIcon from "../assets/UserIcons/Copy.svg";
import knowledgeBaseIcon from "../assets/UserIcons/knowledgeBase.svg";
import linkIcon from "../assets/UserIcons/weblink.svg";
import globalIcon from "../assets/UserIcons/global.svg";
import reloadIcon from "../assets/UserIcons/Reload.svg";
import speakerIcon from "../assets/UserIcons/Speaker.svg";
import onlineIcon from "../assets/UserIcons/OnlineMode.svg";
import offlineIcon from "../assets/UserIcons/OfflineModeBlack.svg";
import tokenIcon from "../assets/UserIcons/token_icon.svg";
import Mermaid from "./markdown/Mermaid";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import arrow from "../assets/icons/Arrow left.svg";
import api from "../api";
import { showErrorToast, showSuccessToast } from "../utils/toastUtils";
import {
  exportBotMessageAsDocx,
  exportBotMessageAsPdf,
} from "../utils/chatMessageExport";
import { markdownToPlainText } from "../utils/markdownToPlainText";

// ============================================================================
// RELATED QUESTIONS HELPERS
// ============================================================================
const sanitizeRelatedQuestion = (text = "") => {
  if (typeof text !== "string") return "";

  return (
    text
      // remove markdown bold/italic
      .replace(/\*{1,3}/g, "")

      // remove unicode dashes & bullets
      .replace(/^[\s—–•\-→➜]+/g, "")

      // remove numbered or bullet lists (1. 2) - *)
      .replace(/^\s*(\d+[\.)]|[-*+])\s+/g, "")

      // remove headings
      .replace(/^#+\s*/g, "")

      // normalize spaces
      .replace(/\s+/g, " ")
      .trim()
  );
};

const getMimeTypeFromExtension = (filename) => {
  const ext = String(filename || "")
    .split(".")
    .pop()
    .toLowerCase();

  const mimeMap = {
    pdf: "application/pdf",
    txt: "text/plain",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    csv: "text/csv",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    json: "application/json",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
    mp3: "audio/mpeg",
    wav: "audio/wav",
    mp4: "video/mp4",
  };

  return mimeMap[ext] || "application/octet-stream";
};

const runBackendToasts = (toasts, fallbackMessage, fileName) => {
  const formatToastMessage = (msg, fname) => {
    if (!msg) return "";
    if (!fname) return String(msg);
    return String(msg).replace(/\{fileName\}/g, fname);
  };

  if (!Array.isArray(toasts) || toasts.length === 0) {
    if (fallbackMessage)
      showErrorToast(formatToastMessage(fallbackMessage, fileName));
    return;
  }

  const count = toasts.length;
  const autoCloseTime = count <= 2 ? 2200 : count <= 4 ? 1800 : 1400;
  const gap = autoCloseTime + 150;

  toasts.forEach((toast, index) => {
    setTimeout(() => {
      const options = {
        autoClose: autoCloseTime,
        toastId: `training-${index}-${toast.message}`,
      };

      const message = formatToastMessage(toast.message, fileName);

      if (toast.type === "success") {
        showSuccessToast(message, options);
      } else {
        showErrorToast(message, options);
      }
    }, index * gap);
  });
};

const extractRelatedQuestions = (text = "") => {
  if (!text || typeof text !== "string") {
    return { cleanText: text, relatedQuestions: [] };
  }

  const patterns = [
    /brief\s+follow[-\s]?up(?: questions?| suggestions?)?:?/i,
    /follow[-\s]?up(?: questions?| suggestions?)?:?/i,
    /related questions?:?/i,
    /suggested questions?:?/i,
    /you may also ask:?/i,
  ];

  let matchIndex = -1;
  let matchLength = 0;

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      matchIndex = match.index;
      matchLength = match[0].length;
      break;
    }
  }

  // If no follow-up section found
  if (matchIndex === -1) {
    // fallback: bold question at end
    const boldMatch = text.match(/\*\*(.+\?)\*\*\s*$/);
    if (boldMatch) {
      return {
        cleanText: text.replace(boldMatch[0], "").trim(),
        // relatedQuestions: [boldMatch[1]],
        relatedQuestions: [sanitizeRelatedQuestion(boldMatch[1])],
      };
    }

    return { cleanText: text, relatedQuestions: [] };
  }

  const mainText = text.slice(0, matchIndex).trim();
  const followUpText = text.slice(matchIndex + matchLength).trim();

  const questions = followUpText
    .split(/[\n•\-—]+/)
    .map((line) => sanitizeRelatedQuestion(line))
    .filter((q) => q && q.endsWith("?"));

  return {
    cleanText: mainText,
    relatedQuestions: [...new Set(questions)],
  };
};

// ============================================================================
// SOURCES HELPERS
// ============================================================================

const stripSourcesFromMarkdown = (markdown = "") => {
  return markdown
    .replace(/(---\s*)?(\*\*|\#\#\#)\s*(🔗\s*)?sources?:?[\s\S]*$/i, "")
    .trim();
};

const extractSourcesFromMarkdown = (markdown = "") => {
  if (typeof markdown !== "string") return [];

  const urls = markdown.match(/https?:\/\/[^\s)]+/g) || [];
  return [...new Set(urls)];
};

const resolveSources = (msg) => {
  if (!msg) return [];

  // 1️⃣ Prefer backend citations
  if (Array.isArray(msg.citations) && msg.citations.length > 0) {
    return [...new Set(msg.citations)];
  }

  // 2️⃣ Fallback: extract from markdown response
  if (typeof msg.message === "string") {
    return extractSourcesFromMarkdown(msg.message);
  }

  return [];
};

const normalizeUrl = (url) => {
  if (!url) return "#";
  return url.startsWith("http") ? url : `https://${url}`;
};

const getDomain = (url) => {
  try {
    return new URL(normalizeUrl(url)).hostname.replace("www.", "");
  } catch {
    return url;
  }
};

// ============================================================================
// MEDIA DETECTION UTILITIES
// ============================================================================

const MEDIA_PATTERNS = {
  // Images: .png, .jpg, .jpeg, .gif, .webp (with optional query params)
  image: /(https?:\/\/[^\s]+?\.(png|jpg|jpeg|gif|webp))(\?[^\s]*)*/i,
  // Videos: .mp4, .webm, .ogg (with optional query params)
  video: /(https?:\/\/[^\s]+?\.(mp4|webm|ogg))(\?[^\s]*)*/i,
};

/**
 * Detect media type from URL
 * @param {string} url - The URL to check
 * @returns {string|null} - 'image', 'video', or null
 */
const detectMediaType = (url) => {
  if (!url || typeof url !== "string") return null;

  const trimmed = url.trim();

  if (MEDIA_PATTERNS.video.test(trimmed)) return "video";
  if (MEDIA_PATTERNS.image.test(trimmed)) return "image";

  return null;
};

/**
 * Extract media URL from markdown-wrapped content
 * @param {string} content - Raw markdown content
 * @returns {string|null} - Extracted URL or null
 */
const extractMediaUrl = (content) => {
  if (!content || typeof content !== "string") return null;

  let url = content.trim();

  // Remove markdown image syntax: ![alt](url)
  if (url.startsWith("![")) {
    url = url.replace(/^!\[.*?\]\(/, "").replace(/\)$/, "");
  }

  // Remove markdown link syntax: [text](url)
  if (url.startsWith("[") && url.includes("](")) {
    url = url.replace(/^\[.*?\]\(/, "").replace(/\)$/, "");
  }

  // Remove surrounding quotes
  url = url.replace(/^["']|["']$/g, "");

  // Decode HTML entities
  url = url.replace(/&amp;/g, "&").replace(/&quot;/g, '"');

  return url.trim();
};

/**
 * Process message content to detect and wrap ONLY OpenAI DALL·E image URLs
 * @param {string} message - The message content
 * @returns {string} - Processed message with OpenAI images wrapped in markdown
 */
const processMessageForMedia = (message) => {
  if (!message || typeof message !== "string") return message;

  let processed = message;

  // ✅ Only allow OpenAI DALL·E Azure Blob images
  const OPENAI_IMAGE_DOMAIN =
    "https://oaidalleapiprodscus.blob.core.windows.net/";

  // 1️⃣ Convert markdown links pointing to OpenAI image → image markdown
  processed = processed.replace(
    /\[([^\]]*)\]\((https:\/\/oaidalleapiprodscus\.blob\.core\.windows\.net\/[^\s)]+)\)/gi,
    "![]($2)",
  );

  // 2️⃣ Convert plain OpenAI image URLs → image markdown
  processed = processed.replace(
    /(https:\/\/oaidalleapiprodscus\.blob\.core\.windows\.net\/[^\s<>"']+)/gi,
    "![]($1)",
  );

  return processed;
};

const SourcesPill = ({ sources }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // ⬇️ Close on outside click
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (!sources || sources.length === 0) return null;

  return (
    <div className="sources-container" ref={containerRef}>
      <button className="sources-pill" onClick={() => setOpen((prev) => !prev)}>
        <span className="icon">🌐</span>
        <span className="icon-badge">{sources.length}</span>
        {/* 🔗 <span className="web-text">{sources.length}</span> */}
      </button>

      {/* <img
        src={globalIcon}
        alt="Source Link"
        className="icon-image"
        role="button"
        title="Source Link"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: "18px",
          height: "18px",
          cursor: "pointer",
        }}
      /> */}

      {open && (
        <div className="sources-popover overflow-hidden">
          <div className="sources-title">Web Search Source:</div>
          <ul>
            {sources.map((src, i) => (
              <li
                title={normalizeUrl(src)}
                key={i}
                className="flex-warp w-100 text-truncate mb-1 sources-items-list"
              >
                <a
                  href={normalizeUrl(src)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {getDomain(src)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

function sanitizeMarkdown(md = "") {
  return (
    md
      // Remove trailing bold markers
      .replace(/\*{2,}\s*$/g, "")
      // Remove trailing list markers
      .replace(/^\s*[-*+]\s*$/gm, "")
      // Remove trailing blockquote markers
      .replace(/^\s*>\s*$/gm, "")
      // Collapse excessive newlines
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  );
}

// ============================================================================
// RAG SOURCES COMPONENT
// ============================================================================

const sanitizeFilename = (name = "") => {
  if (typeof name !== "string") return "";

  /*return name
    .replace(/^https?:\/\//i, "") // remove protocol
    .replace(/[#?].*$/, "") // remove query/hash
    .split("/")
    .pop()
    .replace(/\s+/g, " ")
    .trim();*/

  return name
    .replace(/^https?:\/\//i, "") // remove protocol
    .replace(/^http?:\/\//i, "") // remove protocol
    .replace(/^www?:\/\//i, "") // remove protocol
    .replace(/\/.*$/, "") // remove everything after first /
    .replace(/[#?].*$/, "") // remove query/hash if any
    .trim();
};

const handleViewFile = (document_id, originalFileName = "") => {
  if (!document_id) return;

  const fileUrl = `${process.env.REACT_APP_API_URL}/uploaded-files/viewSources?document_id=${document_id}`;

  const extension = originalFileName?.includes(".")
    ? originalFileName.split(".").pop().toLowerCase()
    : "";
  console.log("extension", extension);

  const imageExtensions = ["png", "jpg", "jpeg", "webp", "gif"];

  // 🖼 Images → open directly
  if (imageExtensions.includes(extension)) {
    window.open(fileUrl, "_blank", "noopener,noreferrer");
    return;
  }

  // 📕 PDF → browser native viewer
  if (extension === "pdf") {
    window.open(fileUrl, "_blank", "noopener,noreferrer");
    return;
  }

  // 📄 DOC / DOCX / TXT / etc → Google Docs Viewer
  const googleViewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
    fileUrl,
  )}&embedded=true`;

  try {
    window.open(googleViewUrl, "_blank", "noopener,noreferrer");
  } catch {
    window.open(fileUrl, "_blank", "noopener,noreferrer");
  }
};

const RagSourcesPill = ({
  ragSources = [],
  selectable = false,
  selectedFiles = [],
  onToggleFile,
  onNewChat,
  title = "Knowledge Base Source:",
}) => {
  // eslint-disable-next-line no-console
  // console.log("RENDER RagSourcesPill", selectedFiles);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const normalize = (v) => String(v ?? "").trim();
  const key = (v) => normalize(v).toLowerCase();

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const raw = Array.isArray(ragSources) ? ragSources : [];

  if (raw.length === 0) return null;

  const sources = [
    ...new Map(
      raw
        .filter((s) => s?.filename && s?.document_id)
        .map((s) => [s.document_id, s]),
    ).values(),
  ];

  if (sources.length === 0) return null;

  return (
    <div className="sources-container" ref={ref}>
      <button
        className="sources-pill"
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((p) => !p);
        }}
      >
        <span className="icon">📚</span>
        <span className="icon-badge">{sources.length}</span>
      </button>

      {open && (
        <div
          className="sources-popover"
          role="dialog"
          onClick={(e) => e.stopPropagation()}
        >
          {selectable ? (
            <div className="kb-source-header">
              <div className="sources-title m-0">{title}</div>
              {onNewChat && (
                <button
                  type="button"
                  className="kb-source-newchat"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onNewChat?.();
                  }}
                  title="New Chat"
                >
                  <i className="bi bi-chat-left"></i>
                </button>
              )}
            </div>
          ) : (
            <div className="sources-title">{title}</div>
          )}
          <ul className="kb-source-list">
            {sources.map((source) => (
              <li key={source.document_id} className="kb-source-item">
                {selectable && (
                  <input
                    type="checkbox"
                    checked={selectedFiles
                      .map(key)
                      .includes(key(source.filename))}
                    className="kb-source-checkbox"
                    onChange={() => onToggleFile(source)}
                  />
                )}
                <span
                  onClick={() =>
                    handleViewFile(source.document_id, source.filename)
                  }
                  style={{
                    cursor: "pointer",
                    color: "#0d6efd",
                    fontSize: "small",
                  }}
                  className="kb-source-filename"
                  title="Click to view file"
                >
                  📄 {source.filename}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// RELATED QUESTIONS COMPONENT
// ============================================================================

const RelatedQuestions = ({ followUp = [], onSelect }) => {
  if (!Array.isArray(followUp) || followUp.length === 0) return null;

  const questions = [
    ...new Set(
      followUp
        .map((q) => sanitizeRelatedQuestion(q))
        .filter((q) => q && q.endsWith("?")),
    ),
  ];

  if (questions.length === 0) return null;

  return (
    <div className="related-questions">
      <div className="related-title">Related questions</div>
      <div className="related-list">
        {questions.map((q, i) => (
          <button
            key={i}
            className="related-pill"
            onClick={() => onSelect?.(q)}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ChatMessage = React.memo(
  ({
    msg,
    loggedInPic,
    userName,
    fontSize,
    onCopy,
    onRetry,
    onTextToSpeech,
    onRelatedQuestionSend,
    searchActive,
    onUpdateMessage,
    botBusy,
    selectedKbFiles,
    onToggleKbFile,
    onKbNewChat,
  }) => {
    useEffect(() => {
      if (typeof onUpdateMessage !== "function") return;
      if (msg.sender !== "bot" || !msg.startedAt) return;

      // We want a ChatGPT-like "thinking time" (TTFT) timer:
      // - Run only while waiting for the first token (dots shown).
      // - Stop immediately once the first token arrives (streaming starts) and
      //   `response_record_time` is set by the stream manager / typing logic.
      if (!msg.showDots) return;
      if (msg.partialMessage) return;
      if (msg.response_record_time != null) return;

      const interval = setInterval(() => {
        const elapsed = (Date.now() - msg.startedAt) / 1000;

        const liveTime = Number(elapsed.toFixed(2));

        onUpdateMessage(msg.id, { liveResponseTime: liveTime });
      }, 100);

      return () => clearInterval(interval);
    }, [
      msg.startedAt,
      msg.showDots,
      msg.partialMessage,
      msg.response_record_time,
      msg.id,
      onUpdateMessage,
    ]);

    const isBot = msg.sender === "bot";
    const isTyping = Boolean(msg.partialMessage); // ✅ ADD THIS
    const exportMenuRef = useRef(null);
    const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [relatedQuestions, setRelatedQuestions] = useState([]);
    const [isTrainingGeneratedFile, setIsTrainingGeneratedFile] =
      useState(false);
    const [showTrainingModal, setShowTrainingModal] = useState(false);
    const [trainingMode, setTrainingMode] = useState("Global");
    const [resolvedGeneratedFileSize, setResolvedGeneratedFileSize] =
      useState(null);

    const exportMarkdown = useMemo(() => {
      if (!isBot || typeof msg.message !== "string") return "";

      const extracted = extractRelatedQuestions(msg.message);
      let content = extracted.cleanText ?? msg.message;
      content = stripSourcesFromMarkdown(content);
      content = sanitizeMarkdown(content);
      return branding(content);
    }, [isBot, msg.message]);

    useEffect(() => {
      if (!isExportMenuOpen) return;

      const onDocMouseDown = (e) => {
        if (!exportMenuRef.current) return;
        if (exportMenuRef.current.contains(e.target)) return;
        setIsExportMenuOpen(false);
      };

      document.addEventListener("mousedown", onDocMouseDown);
      return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [isExportMenuOpen]);

    const handleExport = async (format) => {
      if (!isBot) return;
      if (msg.showDots) return;
      if (!exportMarkdown) return;
      if (isExporting) return;

      setIsExporting(true);
      try {
        if (format === "pdf") {
          await exportBotMessageAsPdf({
            markdown: exportMarkdown,
            messageId: msg.id,
            title: "",
          });
          return;
        }

        if (format === "docx") {
          await exportBotMessageAsDocx({
            markdown: exportMarkdown,
            messageId: msg.id,
            title: "",
          });
          return;
        }
      } catch (e) {
        console.error("Export failed:", e);
        showErrorToast("Export failed. Please try again.");
      } finally {
        setIsExporting(false);
        setIsExportMenuOpen(false);
      }
    };

    const { selectedMode } = useSelector((state) => state.model);


    const botGeneratedFile = isBot ? msg.bot_generated_file : null;
    const hasGeneratedFile = isBot && Boolean(botGeneratedFile?.url);
    const generatedFileName =
      botGeneratedFile?.name ||
      (botGeneratedFile?.url
        ? decodeURIComponent(
            botGeneratedFile.url.split("?")[0].split("/").pop() ||
              "generated_file",
          )
        : "generated_file");
    const normalizedGeneratedFileSize = Number.isFinite(
      Number(botGeneratedFile?.size),
    )
      && Number(botGeneratedFile.size) > 0
      ? Number(botGeneratedFile.size)
      : Number.isFinite(Number(botGeneratedFile?.file_size)) &&
          Number(botGeneratedFile.file_size) > 0
        ? Number(botGeneratedFile.file_size)
        : resolvedGeneratedFileSize;
    const generatedFileSizeLabel =
      normalizedGeneratedFileSize != null
        ? normalizedGeneratedFileSize >= 1024 * 1024
          ? `${(normalizedGeneratedFileSize / (1024 * 1024)).toFixed(2)} MB`
          : `${(normalizedGeneratedFileSize / 1024).toFixed(2)} KB`
        : null;

    useEffect(() => {
      if (!isBot || !botGeneratedFile?.url || normalizedGeneratedFileSize != null) {
        return;
      }

      let cancelled = false;

      const resolveFileSize = async () => {
        try {
          const headResponse = await fetch(botGeneratedFile.url, {
            method: "HEAD",
          });
          const headLength = Number(headResponse.headers.get("content-length"));

          if (
            !cancelled &&
            headResponse.ok &&
            Number.isFinite(headLength) &&
            headLength > 0
          ) {
            setResolvedGeneratedFileSize(headLength);
            return;
          }
        } catch {}

        try {
          const response = await fetch(botGeneratedFile.url);
          if (!response.ok) return;

          const blob = await response.blob();
          if (!cancelled && Number.isFinite(blob.size) && blob.size > 0) {
            setResolvedGeneratedFileSize(blob.size);
          }
        } catch {}
      };

      setResolvedGeneratedFileSize(null);
      resolveFileSize();

      return () => {
        cancelled = true;
      };
    }, [isBot, botGeneratedFile?.url, normalizedGeneratedFileSize]);
    const handleGeneratedFileDownload = (url, fileName) => {
      if (!url) return;

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "generated_file";
      link.rel = "noopener noreferrer";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    const generatedFileDescription =
      typeof botGeneratedFile?.description === "string" &&
      botGeneratedFile.description.trim()
        ? botGeneratedFile.description.trim()
        : null;

    const openTrainingModal = () => {
      const normalized =
        String(selectedMode || "")
          .trim()
          .toLowerCase() === "personal"
          ? "Personal"
          : "Global";
      setTrainingMode(normalized);
      setShowTrainingModal(true);
    };

    const handleSendGeneratedFileForTraining = async (mode) => {
      const fileUrlFromDbPath =
        botGeneratedFile?.user_files_path && process.env.REACT_APP_SERVER_URL
          ? `${String(process.env.REACT_APP_SERVER_URL).replace(/\/+$/, "")}/${String(
              botGeneratedFile.user_files_path,
            ).replace(/\\/g, "/")}`
          : null;

      const fileUrlToFetch = fileUrlFromDbPath || botGeneratedFile?.url || null;

      if (!fileUrlToFetch) {
        showErrorToast("No generated file found for training.");
        return;
      }

      try {
        setIsTrainingGeneratedFile(true);
        setShowTrainingModal(false);

        const fileResponse = await fetch(fileUrlToFetch);
        if (!fileResponse.ok) throw new Error("Failed to fetch file");

        const blob = await fileResponse.blob();
        const safeFilename =
          botGeneratedFile?.name || generatedFileName || "generated_file";
        const mimeType = blob.type || getMimeTypeFromExtension(safeFilename);

        const file = new File([blob], safeFilename, { type: mimeType });

        const formData = new FormData();
        formData.append("files", file);
        formData.append("mode", String(mode || "global").toLowerCase());
        formData.append("model", "llama-3.3-70b-versatile");

        const token = localStorage.getItem("access_token");
        const uploadUrl = `${process.env.REACT_APP_API_URL}/uploaded-files/newQueryUpload`;

        const response = await api.post(uploadUrl, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          validateStatus: () => true,
        });

        runBackendToasts(
          response.data?.toasts,
          response.data?.status === "error" ? response.data?.message : null,
          safeFilename,
        );

        if (response.status === 200 || response.status === 207) {
          if (
            !Array.isArray(response.data?.toasts) ||
            !response.data?.toasts?.length
          ) {
            showSuccessToast("Sent for training successfully.");
          }
          return;
        }

        showErrorToast(
          response.data?.message || "Failed to send for training.",
        );
      } catch (error) {
        showErrorToast(
          error.response?.data?.message ||
            error.message ||
            "Training failed unexpectedly.",
        );
      } finally {
        setIsTrainingGeneratedFile(false);
      }
    };

    const profile =
      msg.sender === "bot"
        ? "/img/VelaKoda_Fevicon.svg"
        : msg.userPic || loggedInPic;
    const displayName =
      msg.sender === "bot"
        ? branding("ZeroBox")
        : msg.userName || userName || "User";

    const analyzingMessage = "Analyzing the request";
    const understandingUserMessage = "Understanding user intent";
    const interpretingUserContextMessage = "Interpreting user context";
    const evaluatingMessage = "Evaluating response approach";
    const preparingMessage = "Preparing response strategy";
    const formulatingMessage = "Formulating response plan";
    const thinkingMessage = "Thinking";
    const checkingKnowledgeBaseMessage = "Checking knowledge base";
    const webMessage = "Checking web sources";
    const crossInfoMessage = "Cross-checking references";
    const validatingInfoMessage = "Validating information";
    const organizatingInfoMessage = "Organizing response insights";
    const ensuringCRMessage = "Ensuring clarity and relevance";
    const refiningResponseQualityMessage = "Refining response quality";
    const filteringInfoMessage = "Filtering relevant information";
    const maintainingConversationalToneMessage =
      "Maintaining conversational tone";
    const determiningMessage = "Determining best response";
    const structuringMessage = "Structuring response";
    const finalMessage = "Generating response";

    const baseMessages = [
      analyzingMessage,
      understandingUserMessage,
      interpretingUserContextMessage,
      evaluatingMessage,
      preparingMessage,
      formulatingMessage,
      checkingKnowledgeBaseMessage,
      crossInfoMessage,
      validatingInfoMessage,
      organizatingInfoMessage,
      ensuringCRMessage,
      refiningResponseQualityMessage,
      filteringInfoMessage,
      maintainingConversationalToneMessage,
      determiningMessage,
      structuringMessage,
    ];

    const messages = useMemo(() => {
      return searchActive
        ? [...baseMessages, webMessage, finalMessage]
        : [...baseMessages, finalMessage];
      // NOTE: baseMessages/webMessage/finalMessage are stable strings; memoization
      // prevents the rolling timer from resetting on every streaming re-render.
    }, [searchActive]);

    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [displayMessage, setDisplayMessage] = useState(messages[0]);

    const isDefaultMessage = messages.includes(displayMessage);
    useEffect(() => {
      if (currentMessageIndex >= messages.length - 1) return;

      const timer = setTimeout(() => {
        setCurrentMessageIndex((prev) => prev + 1);
        setDisplayMessage(messages[currentMessageIndex + 1]);
      }, 3000); // ⬅️ 3 seconds per step

      return () => clearTimeout(timer);
    }, [currentMessageIndex, messages]);

    useEffect(() => {
      setCurrentMessageIndex(0);
      setDisplayMessage(messages[0]);
    }, [searchActive]);

    useEffect(() => {
      if (!isBot || msg.showDots) return;

      // 1️⃣ Prefer backend follow_up (already array)
      if (Array.isArray(msg.follow_up) && msg.follow_up.length > 0) {
        const sanitized = [
          ...new Set(
            msg.follow_up
              .map((q) => sanitizeRelatedQuestion(q))
              .filter((q) => q && q.endsWith("?")),
          ),
        ];

        setRelatedQuestions(sanitized);
        return;
      }

      // 2️⃣ Fallback: extract from message text
      if (typeof msg.message === "string") {
        const extracted = extractRelatedQuestions(msg.message);
        setRelatedQuestions(extracted.relatedQuestions || []);
      }
    }, [msg.message, msg.follow_up, msg.showDots]);

    const getFileExtension = (file) => {
      if (file?.file_extension) return file.file_extension.toLowerCase();

      return file?.url?.split("?")[0]?.split(".")?.pop()?.toLowerCase();
    };

    const isZipFile = getFileExtension(botGeneratedFile) === "zip";

    return (
      <div
        className={`d-flex ${
          isBot ? "flex-row" : "flex-row-reverse"
        } align-items-start my-4`}
      >
        <div className="d-flex flex-column chat-message-container">
          <div
            className={`chat-bubble ${isBot ? "bot-bubble" : "user-bubble"}`}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <img
                src={profile}
                className={isBot ? "bot-avatar" : "chat-avatar"}
                alt="avatar"
              />
              <strong>{displayName}</strong>
              <small>{msg.time}</small>

              {isBot && (
                <small style={{ fontStyle: "italic", color: "#888" }}>
                  (
                  {(() => {
                    // Prefer frozen TTFT once the first token arrives.
                    const value =
                      msg.response_record_time ??
                      (msg.showDots
                        ? msg.liveResponseTime
                        : msg.response_time ?? msg.liveResponseTime);

                    const numeric = Number(value ?? 0);
                    return Number.isFinite(numeric) ? numeric.toFixed(2) : "0.00";
                  })()}{" "}
                  )
                </small>
              )}
            </div>

            <div className="formatted-text" style={{ fontSize }}>
              {msg.files && msg.files.length > 0 && (
                <div className="attached-files">
                  {msg.files.map((file, idx) => (
                    <div
                      className="file-bubble d-flex align-items-center gap-2 p-2 rounded"
                      key={idx}
                    >
                      <img src={fileIcon} alt="File" width="22" />
                      <div className="d-flex flex-column">
                        <small title={file.name} className="file-name">
                          {file.name}
                        </small>
                        <small className="file-size">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isBot && botGeneratedFile?.url && (
                <div className="generated-file-card d-flex align-items-center justify-content-between p-2 rounded mt-2">
                  <div className="d-flex align-items-center gap-2 overflow-hidden">
                    <img src={fileIcon} alt="Generated File" width="20" />
                    <div className="d-flex flex-column overflow-hidden">
                      <small
                        className="file-name text-truncate"
                        title={generatedFileName}
                      >
                        {generatedFileName}
                      </small>
                      <small className="file-size">
                        {generatedFileSizeLabel || "File size unavailable"}
                      </small>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    {!isZipFile && (
                      <button
                        type="button"
                        className="generated-file-download"
                        title={
                          isTrainingGeneratedFile
                            ? "Sending for training..."
                            : "Send for training"
                        }
                        disabled={isTrainingGeneratedFile}
                        onClick={openTrainingModal}
                        style={{
                          opacity: isTrainingGeneratedFile ? 0.6 : 1,
                          cursor: isTrainingGeneratedFile
                            ? "not-allowed"
                            : "pointer",
                        }}
                      >
                        <img
                          src={trainIcon}
                          alt="Send for training"
                          width="18"
                          height="18"
                        />
                      </button>
                    )}
                    <button
                      type="button"
                      className="generated-file-download"
                      title="Download file"
                      onClick={() =>
                        handleGeneratedFileDownload(
                          botGeneratedFile.url,
                          generatedFileName,
                        )
                      }
                    >
                      <img
                        src={downloadIcon}
                        alt="Download"
                        width="18"
                        height="18"
                      />
                    </button>
                  </div>
                </div>
              )}

              {showTrainingModal && (
                <div
                  className="modal modal-backdrop-custom d-block"
                  tabIndex="-1"
                >
                  <div className="modal-dialog-centered">
                    <div className="modal-content mx-auto">
                      <div className="modal-header border-bottom justify-content-center position-relative">
                        <h5 className="modal-title m-0">Send for Training</h5>
                        <button
                          type="button"
                          className="modal-close-btn"
                          aria-label="Close"
                          disabled={isTrainingGeneratedFile}
                          onClick={() => {
                            if (!isTrainingGeneratedFile)
                              setShowTrainingModal(false);
                          }}
                        >
                          <img src={arrow} className="icon-image" alt="Close" />
                        </button>
                      </div>

                      <div className="modal-body text-center">
                        <p className="refresh-prompts">
                          Select mode before sending this file for training:
                        </p>

                        <div className="d-flex justify-content-center md:gap-4 my-3">
                          {["Global", "Personal"].map((mode) => (
                            <label
                              key={mode}
                              className="form-check custom-form-check d-flex align-items-center gap-2 pl-0 p-2"
                            >
                              <input
                                type="radio"
                                name="trainingMode"
                                className="custom-radio"
                                value={mode}
                                checked={trainingMode === mode}
                                onChange={() => setTrainingMode(mode)}
                              />
                              <span className="refresh-prompts">{mode}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="d-flex justify-content-center mb-3 gap-3">
                        <button
                          type="button"
                          className="w-25 cancle-btn"
                          disabled={isTrainingGeneratedFile}
                          onClick={() => setShowTrainingModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="w-25 custom-btn"
                          disabled={isTrainingGeneratedFile}
                          onClick={() =>
                            handleSendGeneratedFileForTraining(trainingMode)
                          }
                        >
                          {isTrainingGeneratedFile ? "Sending..." : "Send"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {isBot && msg.showDots ? (
                // <div className="d-flex align-items-center message-animate">
                <div
                  key={currentMessageIndex}
                  className={`d-flex align-items-center ${
                    currentMessageIndex % 1 === 0 ? "message-animate" : ""
                  }`}
                >
                  <span
                    style={{
                      fontSize: isDefaultMessage ? "13px" : "",
                      opacity: 0.9,
                      fontStyle: "italic",
                    }}
                    className="me-1"
                  >
                    {displayMessage}
                  </span>
                  <div className="typing-indicator d-flex">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                </div>
              ) : isBot ? (
                <div className="markdown-renderer">
                  <ReactMarkdown
                    remarkPlugins={[
                      remarkGfm,
                      remarkSlug,
                      remarkAutolinkHeadings,
                    ]}
                    rehypePlugins={[]} // keep rehypeRaw
                    components={{
                      table: ({ node, ...props }) => (
                        <TableWithCopy {...props} />
                      ),

                      a: ({ href, children, ...props }) => {
                        if (
                          href &&
                          (href.includes("youtube.com") ||
                            href.includes("youtu.be"))
                        ) {
                          // Avoid iframe remount flicker during streaming updates.
                          if (isTyping) {
                            return (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                {...props}
                              >
                                {children}
                              </a>
                            );
                          }
                          return <YouTubeEmbed url={href} />;
                        }

                        if (href && detectMediaType(href) === "video") {
                          return <VideoWithZoom src={href} />;
                        }

                        return (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...props}
                          >
                            {children}
                          </a>
                        );
                      },

                      code({ inline, className, children }) {
                        const match = /language-(\w+)/.exec(className || "");
                        if (!inline && match) {
                          return (
                            <CodeBlockWithCopy
                              language={match[1]}
                              value={String(children).replace(/\n$/, "")}
                            />
                          );
                        }

                        if (inline) {
                          return <InlineCode>{children}</InlineCode>;
                        }

                        return <code>{children}</code>;
                      },

                      img: (props) => <ImageWithZoom {...props} />,

                      li: ({ children, ...props }) => {
                        const text = String(children).replace(/\s/g, "");
                        if (!text) return null;
                        return <li {...props}>{children}</li>;
                      },
                    }}
                  >
                    {(() => {
                      // 🔥 Choose correct source
                      let content = isTyping
                        ? msg.partialMessage // streaming text
                        : msg.message; // final text

                      if (typeof content === "string") {
                        // ✅ NO follow-up extraction here anymore
                        content = processMessageForMedia(content);
                        content = stripSourcesFromMarkdown(content);
                        content = sanitizeMarkdown(content);
                      }

                      return content;
                    })()}
                  </ReactMarkdown>
                </div>
              ) : (
                // 👤 USER → Preserve textarea formatting
                <div
                  className="formatted-text"
                  style={{
                    fontSize,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {msg.message}
                </div>
              )}

            </div>
          </div>

          {!msg.showDots && (
            <div
              className={`d-flex bot-below-icon gap-2 gap-md-3 align-items-center mt-1 ${
                isBot
                  ? "justify-content-start ms-3"
                  : "justify-content-end me-3 "
              }`}
            >
              {isBot ? (
                <>
                  <img
                    src={speakerIcon}
                    alt="Play Sound"
                    className="icon-image"
                    role="button"
                    title="Play Sound"
                    onClick={() => onTextToSpeech(msg.message)}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                    }}
                  />

                  <img
                    src={cpyIcon}
                    className="icon-image"
                    alt="Copy"
                    role="button"
                    title="Copy"
                    onClick={() => onCopy(markdownToPlainText(exportMarkdown || msg.message))}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                    }}
                  />

                  <img
                    src={reloadIcon}
                    className="icon-image pointer"
                    alt="Retry"
                    role="button"
                    title="Retry"
                    onClick={() => onRetry(msg.id)}
                    style={{ width: "16px", height: "16px" }}
                  />

                  {/* <img
                    src={reloadIcon}
                    className={`icon-image pointer ${
                      botBusy ? "disabled" : ""
                    }`}
                    alt="Retry"
                    title={botBusy ? "Bot is responding" : "Retry"}
                    onClick={() => {
                      if (!botBusy) onRetry(msg.id);
                    }}
                    style={{ width: "16px", height: "16px" }}
                  /> */}

                  {!msg.showDots && (
                    <SourcesPill sources={resolveSources(msg) || []} />
                  )}

                  {!msg.showDots && (
                    <RagSourcesPill
                      ragSources={msg.rag_sources}
                      selectable={true}
                      selectedFiles={selectedKbFiles || []}
                      onToggleFile={onToggleKbFile}
                      onNewChat={onKbNewChat}
                      title="Knowledge Base Source:"
                    />
                  )}

                  {!msg.showDots && (
                    <div className="chat-export-icon-anchor" ref={exportMenuRef}>
                      <button
                        type="button"
                        className="chat-download-icon-btn"
                        onClick={() => {
                          if (hasGeneratedFile) {
                            handleGeneratedFileDownload(
                              botGeneratedFile.url,
                              generatedFileName,
                            );
                            return;
                          }

                          setIsExportMenuOpen((v) => !v);
                        }}
                        disabled={isExporting}
                        title="Download this bot message"
                      >
                        <i className="bi bi-download" />
                      </button>

                      {!hasGeneratedFile && isExportMenuOpen && (
                        <div className="chat-export-menu" role="menu">
                          <button
                            type="button"
                            className="chat-export-menu-item"
                            onClick={() => handleExport("pdf")}
                            disabled={isExporting}
                          >
                            Download as PDF
                          </button>
                          <button
                            type="button"
                            className="chat-export-menu-item"
                            onClick={() => handleExport("docx")}
                            disabled={isExporting}
                          >
                            Download as DOCX
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {msg.model_generic_name && (
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={
                          String(msg?.model_type ?? "")
                            .trim()
                            .toLowerCase() === "online"
                            ? onlineIcon
                            : offlineIcon
                        }
                        alt="mode-icon"
                        style={{ width: "20px", height: "18px" }}
                        className="icon-image"
                      />
                      <small
                        style={{
                          fontSize: "0.8rem",
                          color: "var(--text-muted, #888)",
                          fontStyle: "italic",
                        }}
                      >
                        {msg.model_generic_name}
                      </small>
                    </div>
                  )}

                  {(msg.input_tokens || msg.output_tokens) && (
                    <div
                      className="chat-export-anchor ms-2"
                    >
                      {msg.input_tokens && msg.output_tokens && (
                        <div className="chat-token-io">
                          <img
                            src={tokenIcon}
                            alt="Tokens"
                            title="Token Usage"
                            className="icon-image"
                            style={{ width: "16px", height: "16px" }}
                          />
                          <span>
                            {msg.input_tokens} → {msg.output_tokens}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <img
                    src={cpyIcon}
                    className="icon-image pointer"
                    alt="Copy"
                    role="button"
                    title="Copy"
                    onClick={() => onCopy(msg.message)}
                    style={{ width: "16px", height: "16px" }}
                  />
                </>
              )}
            </div>
          )}
          {isBot && !msg.showDots && relatedQuestions.length > 0 && (
            <div className="related-card mt-2">
              <div className="related-header formatted-text">
                Related Questions
              </div>

              <ul className="related-list">
                {relatedQuestions.map((q, i) => (
                  <li
                    key={i}
                    className="related-item p-1 px-md-2 py-md-1"
                    onClick={() => onRelatedQuestionSend?.(q)}
                  >
                    <img
                      className="related-arrow icon-image"
                      style={{ height: "16px", width: "16px" }}
                      src={arrow}
                      alt="arrow"
                    />
                    <span className="related-text ">{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if these specific properties change
    return (
      prevProps.msg.id === nextProps.msg.id &&
      prevProps.msg.message === nextProps.msg.message &&
      prevProps.msg.partialMessage === nextProps.msg.partialMessage &&
      prevProps.msg.showDots === nextProps.msg.showDots &&
      prevProps.msg.model_generic_name === nextProps.msg.model_generic_name &&
      prevProps.msg.liveResponseTime === nextProps.msg.liveResponseTime && // ✅ ADD THIS
      prevProps.msg.response_record_time ===
        nextProps.msg.response_record_time &&
      prevProps.msg.bot_generated_file?.url ===
        nextProps.msg.bot_generated_file?.url &&
      prevProps.fontSize === nextProps.fontSize &&
      // 🔥 ADD THIS (MAIN FIX)
      JSON.stringify(prevProps.selectedKbFiles) ===
        JSON.stringify(nextProps.selectedKbFiles)
    );
  },
);

export default ChatMessage;
